import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * RouteProgressBar
 *
 * A premium adtech-style top progress bar that animates on every route change.
 *
 * Behavior:
 *   - On route change, snaps to 0 and quickly animates to ~80% (250ms).
 *   - Holds at ~80% while the new route renders.
 *   - Completes to 100% (200ms) and fades out (180ms) once the page is settled.
 *
 * Style:
 *   - 2.5px height, fixed at the very top, full-width.
 *   - Gradient: cyan → blue → violet with a soft outer glow.
 *
 * Accessibility:
 *   - Hidden from assistive tech (decorative).
 *   - Respects `prefers-reduced-motion: reduce` by skipping easing curves.
 */
export default function RouteProgressBar() {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timersRef = useRef([]);
  const firstMountRef = useRef(true);

  useEffect(() => {
    // Skip the very first render so the bar doesn't flash on initial load.
    if (firstMountRef.current) {
      firstMountRef.current = false;
      return;
    }

    // Clear any in-flight timers from a previous transition.
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    setVisible(true);
    // Reset to 0 on the next frame so the CSS transition runs.
    requestAnimationFrame(() => setProgress(2));

    // Phase 1: ramp toward 80% quickly (~250ms).
    timersRef.current.push(setTimeout(() => setProgress(80), 30));

    // Phase 2: complete to 100% (~250ms after start).
    timersRef.current.push(setTimeout(() => setProgress(100), 320));

    // Phase 3: fade out the bar.
    timersRef.current.push(setTimeout(() => setVisible(false), 600));

    // Phase 4: reset value once invisible so the next change starts fresh.
    timersRef.current.push(setTimeout(() => setProgress(0), 850));

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [location.pathname]);

  return (
    <div
      aria-hidden="true"
      className="route-progress-bar"
      data-testid="route-progress-bar"
      data-visible={visible ? 'true' : 'false'}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 2.5,
        zIndex: 1000,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: 'opacity 180ms ease-out',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${progress}%`,
          background:
            'linear-gradient(90deg, #22D3EE 0%, #3B82F6 50%, #8B5CF6 100%)',
          boxShadow:
            '0 0 12px rgba(34, 211, 238, 0.55), 0 0 22px rgba(139, 92, 246, 0.35)',
          transition: 'width 320ms cubic-bezier(0.22, 1, 0.36, 1)',
          borderRadius: '0 2px 2px 0',
        }}
      />
      {/* Trailing glow streak — gives the bar a subtle "data flow" feel */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: `calc(${progress}% - 60px)`,
          width: 80,
          height: '100%',
          background:
            'linear-gradient(90deg, transparent, rgba(125, 220, 255, 0.85), transparent)',
          filter: 'blur(2px)',
          opacity: progress > 0 && progress < 100 ? 0.9 : 0,
          transition:
            'left 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease-out',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
